/**
 * @file 灰度发布 API
 * @description 封装灰度命中查询和管理员灰度配置维护接口。
 */
import instance from '@/api/core/request'
import type { ApiResponse } from '@/types/apiResponse'
import type { AxiosResponse } from 'axios'
import type { CustomAxiosRequestConfig } from '@/api/core/request'

/** 后端统一响应结构。 */
/** 无数据响应。 */
type ResultVoid = ApiResponse<void>

/** 当前组员灰度命中响应。 */
export interface GrayCurrentDecisionResponse {
  /** 功能命中表，key 为功能标识，value 为是否进入灰度。 */
  features: Record<string, boolean>
}

/** 灰度功能管理项。 */
export interface GrayFeatureAdminItem {
  /** 功能唯一标识。 */
  featureKey: string
  /** 功能展示名称。 */
  featureName: string
  /** 功能说明。 */
  description: string
  /** 功能是否启用。 */
  enabled: boolean
  /** 灰度比例，0 到 10000 分别表示 0% 到 100%。 */
  rolloutRate: number
  /** 体验组员 ID 列表。 */
  testerMemberIds: number[]
  /** 创建时间。 */
  createdAt: string
  /** 更新时间。 */
  updatedAt: string
}

/** 灰度功能更新请求。 */
export interface GrayFeatureUpdatePayload {
  /** 功能展示名称。 */
  featureName: string
  /** 功能说明。 */
  description: string
  /** 功能是否启用。 */
  enabled: boolean
  /** 灰度比例，0 到 10000 分别表示 0% 到 100%。 */
  rolloutRate: number
  /** 体验组员 ID 列表。 */
  testerMemberIds: number[]
}

/** 查询当前组员的灰度命中结果。 */
export const getCurrentGrayFeatures = (): Promise<
  AxiosResponse<ApiResponse<GrayCurrentDecisionResponse>>
> =>
  instance<ApiResponse<GrayCurrentDecisionResponse>>({
    url: '/api/gray/features/current',
    method: 'GET',
    suppressError: true
  } as CustomAxiosRequestConfig)

/** 查询管理员灰度功能配置列表。 */
export const listGrayFeatures = (): Promise<
  AxiosResponse<ApiResponse<GrayFeatureAdminItem[]>>
> =>
  instance<ApiResponse<GrayFeatureAdminItem[]>>({
    url: '/api/admin/gray/features',
    method: 'GET'
  })

/**
 * 新增或更新灰度功能配置。
 * @param featureKey 功能唯一标识。
 * @param payload 灰度功能配置。
 */
export const updateGrayFeature = (
  featureKey: string,
  payload: GrayFeatureUpdatePayload
): Promise<AxiosResponse<ResultVoid>> =>
  instance<ResultVoid>({
    url: `/api/admin/gray/features/${encodeURIComponent(featureKey)}`,
    method: 'PUT',
    data: payload
  })

/**
 * 回退单个灰度功能到旧逻辑。
 * @param featureKey 功能唯一标识。
 */
export const rollbackGrayFeature = (
  featureKey: string
): Promise<AxiosResponse<ResultVoid>> =>
  instance<ResultVoid>({
    url: `/api/admin/gray/features/${encodeURIComponent(featureKey)}/rollback`,
    method: 'POST'
  })

/** 回退全部灰度功能到旧逻辑。 */
export const rollbackAllGrayFeatures = (): Promise<AxiosResponse<ResultVoid>> =>
  instance<ResultVoid>({
    url: '/api/admin/gray/rollback-all',
    method: 'POST'
  })
